import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { timer } from 'rxjs';
import { delay, filter, mergeMap, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { differenceInMilliseconds, addHours, startOfHour } from 'date-fns/esm';
import { zefRemoveError } from '@zerops/zef/errors';
import { zefRemoveProgress } from '@zerops/zef/progress';
import { zefResetState } from '@zerops/zef/core';

import { UserEntity } from '@zerops/zerops/core/user-base';
import {
  onWebsocketMessageDispatchUpdateEntities,
  onWebsocketMessageDispatchAddRemoveEntities
} from '@zerops/zef/entities';
import { ResourceConsumptionBaseApi } from './resource-consumption-base.api';
import * as ResourceConsumptionBaseActions from './resource-consumption-base.action';
import {
  selectResourceConsumptionBaseGroupedData,
  selectResourceConsumptionTrackedItems
} from './resource-consumption-base.selector';
import {
  resourceConsumptionGroupExists,
  buildResourceConsumptionGroupSearchRequest
} from './resource-consumption-base.utils';
import { FEATURE_NAME } from './resource-consumption-base.constant';
import { ResourceConsumptionEntity } from './resource-consumption-base.entity';
import {
  createTimeSeriesGroupFetchEffect,
  createTimeSeriesWebSocketUpdateEffect,
  createTimeSeriesTrackingEffect
} from '../shared';

@Injectable()
export class ResourceConsumptionBaseEffect {

  private _activeClientId$ = this._userEntity.activeClientId$.pipe(filter((d) => !!d));

  private _hourlyTimer$ = timer(
    differenceInMilliseconds(startOfHour(addHours(new Date(), 1)), new Date()) + 500,
    60 * 60 * 1000
  );

  // Use shared tracking effect
  private _onResourceConsumptionGroupTrackItems$ = createTimeSeriesTrackingEffect({
    groupActionType: ResourceConsumptionBaseActions.resourceConsumptionGroup,
    trackActionCreator: ResourceConsumptionBaseActions.trackResourceConsumptionGroupItem
  })(this._actions$);

  // Use shared entity effects
  private _setupAddRemoveMessage$ = createEffect(() => this._actions$.pipe(
    onWebsocketMessageDispatchAddRemoveEntities(this._resourceConsumptionEntity)
  ));

  private _onUpdateStreamMessage$ = createEffect(() => this._actions$.pipe(
    onWebsocketMessageDispatchUpdateEntities(this._resourceConsumptionEntity)
  ));

  // Use shared group fetch effect
  private _onResourceConsumptionGroup$ = createTimeSeriesGroupFetchEffect({
    groupActionType: ResourceConsumptionBaseActions.resourceConsumptionGroup,
    apiCall: this._api.resourceConsumptionGroup.bind(this._api),
    existsCheck: resourceConsumptionGroupExists,
    buildRequest: buildResourceConsumptionGroupSearchRequest,
    successActionCreator: ResourceConsumptionBaseActions.resourceConsumptionGroupSuccess,
    failActionCreator: ResourceConsumptionBaseActions.resourceConsumptionGroupFail,
    cancelActionCreator: ResourceConsumptionBaseActions.resourceConsumptionGroupCancel,
    selectGroupedData: selectResourceConsumptionBaseGroupedData,
    activeClientId$: this._activeClientId$
  })(this._actions$, this._store);

  // Use shared WebSocket update effect
  private _onResourceConsumptionGroupUpdate$ = createTimeSeriesWebSocketUpdateEffect({
    featureName: FEATURE_NAME,
    updateActionCreator: ResourceConsumptionBaseActions.resourceConsumptionGroupUpdate
  })(this._actions$);

  // Cancel handler effect - cleans up progress and error states
  private _onResourceConsumptionGroupCancel$ = createEffect(() => this._actions$.pipe(
    ofType(ResourceConsumptionBaseActions.resourceConsumptionGroupCancel),
    delay(0),
    mergeMap(() => [
      zefRemoveError(ResourceConsumptionBaseActions.resourceConsumptionGroup.type),
      zefRemoveProgress(ResourceConsumptionBaseActions.resourceConsumptionGroup.type)
    ])
  ));

  // Resource consumption is loaded on demand in project detail, not globally auto-loaded

  // Hourly update effect
  private _onResourceConsumptionGroupHourlyUpdate$ = createEffect(() =>
    this._hourlyTimer$.pipe(
      takeUntil(this._actions$.pipe(ofType(zefResetState))),
      withLatestFrom(this._store.pipe(select(selectResourceConsumptionTrackedItems))),
      filter(([, trackedItems]: [any, any[]]) => trackedItems.length > 0),
      switchMap(([, trackedItems]: [any, any[]]) => trackedItems
        .filter((q: any) => q.data.groupRange.hasCurrent)
        .filter((q: any) => ['1h', '1d'].includes(q.data.groupRange.timeGroupBy))
        .map((q: any) => ResourceConsumptionBaseActions.resourceConsumptionGroup({
          data: {
            ...q.data,
            isPeriodUpdate: true
          }
        }))
      )
    )
  );

  constructor(
    private _actions$: Actions,
    private _store: Store,
    private _api: ResourceConsumptionBaseApi,
    private _userEntity: UserEntity,
    private _resourceConsumptionEntity: ResourceConsumptionEntity
  ) {}
}
